import { useContext, useState } from 'react';
import { Avatar, Popover, Divider, Modal, Button, Select } from 'antd';
import { UserOutlined, SettingOutlined, LogoutOutlined, CrownOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createAuthClient } from 'better-auth/react';
import config from 'config';
import { EasyDrawContext } from '@/store';
import { UserStore } from '@/store/user';
import { CanvopixTheme } from '@/Layout/Canvopix/types';
import { ROUTER_BETTER_AUTH } from '@/entry/route';
import './styles.less';

const authClient = createAuthClient({
  baseURL: config.domain,
  basePath: '/api/better_auth',
});

// Placeholder PayModal
const PlaceholderPayModal = ({ visible, onClose, onSuccess }: any) => {
  return (
    <Modal
      title="Subscribe"
      open={visible}
      onCancel={onClose}
      footer={null}
    >
      <div className="p-4 text-center">
        <h3 className="text-lg font-bold mb-4">International Payment Gateway</h3>
        <p className="mb-4">Stripe / PayPal integration coming soon.</p>
        <Button type="primary" onClick={() => {
            onSuccess?.();
            onClose();
        }}>
          Simulate Success
        </Button>
      </div>
    </Modal>
  );
};

interface UnifiedAvatarProps {
  className?: string;
  theme?: CanvopixTheme;
}

const ORANGE_ICON = '/orange.webp';

// UnLogin Content
const UnifiedUnLoginPopoverContent = ({ handleLogin, theme }: { handleLogin: () => void, theme?: CanvopixTheme }) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  
  return (
    <div className={classNames('unlogin-popover-content', { 'dark-theme': theme === 'dark' })}>
      <div className="header">
        <div className="title">{t('user.unlock_potential', 'Unlock Potential')}</div>
        <div className="subtitle">{t('user.login_benefits', 'Login to enjoy exclusive benefits')}</div>
      </div>
      
      <div className="benefits">
        <div className="benefits-card">
          {[
            { icon: '💾', text: t('user.save_work', 'Save Unlimited Works') },
            { icon: '☁️', text: t('user.cloud_sync', 'Cloud Sync') },
            { icon: '🎨', text: t('user.templates', 'Exclusive Templates') },
            { icon: '📸', text: t('user.hd_export', 'HD Export') }
          ].map((item, index) => (
            <div key={index} className="benefit-item">
              <span className="icon">{item.icon}</span>
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
      
      <button className="login-button" onClick={handleLogin}>
        {t('user.login_now', 'Login Now')}
      </button>

      <Divider style={{ margin: '12px 0' }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontSize: 12, opacity: 0.9 }}>
          {t('common.language', 'Language')}
        </span>
        <Select
          size="small"
          value={currentLanguage === 'en-US' ? 'en-US' : 'zh-CN'}
          style={{ width: 120 }}
          onChange={(lng) => i18n.changeLanguage(lng)}
          options={[
            { value: 'zh-CN', label: t('common.language_zh', '中文') },
            { value: 'en-US', label: t('common.language_en', 'English') },
          ]}
        />
      </div>
      
      <div className="footer-tip">
        {t('user.free_signup', 'Free Sign up · Start in 30s')}
      </div>
    </div>
  );
};

// Login Content
const UnifiedLoginPopoverContent = observer(({ 
  user, 
  onSetting, 
  onLogout,
  onUpgrade,
  theme
}: { 
  user: UserStore;
  onSetting: () => void;
  onLogout: () => void;
  onUpgrade?: () => void;
  theme?: CanvopixTheme;
}) => {
  const { t, i18n } = useTranslation();
  const isVip = user?.vipLevel > 0;
  const currentLanguage = i18n.resolvedLanguage || i18n.language;

  return (
    <div className={classNames('login-popover-content', { 'dark-theme': theme === 'dark' })}>
      <div className="user-info">
        <div className="user-avatar">
          {user.avatar ? (
            <img src={user.avatar} alt="avatar" className="avatar-img" />
          ) : (
            <div className="avatar-placeholder">
              <UserOutlined />
            </div>
          )}
        </div>
        <div className="user-details">
          <div className="username">
            {user.email || user.nickname || user.userName || t('user.default_name', 'User')}
          </div>
          <div className="user-status">
            {isVip ? (
              <div className="vip-status">
                <CrownOutlined className="vip-icon" />
                <span className="vip-text">PRO</span>
                <span className='vip-renew' onClick={onUpgrade}>{t('user.renew', 'Renew')}</span>
              </div>
            ) : (
              <span className="normal-status">{t('user.free_plan', 'Free Plan')}</span>
            )}
          </div>
        </div>
        <div className="setting-button" onClick={onSetting}>
          <SettingOutlined className="setting-icon" />
        </div>
      </div>

      {!isVip && (
        <>
          <div className="divider"></div>
          <div className="vip-promotion">
            <div className="vip-header">
              <div className="vip-title">
                {t('user.upgrade_title', 'Upgrade to PRO')}
              </div>
              <div className="vip-subtitle">
                {t('user.upgrade_desc', 'Commercial License & More')}
              </div>
            </div>
            <button className="upgrade-button" onClick={onUpgrade}>
              <CrownOutlined className="crown-icon" />
              {t('user.upgrade_btn', 'Upgrade')}
            </button>
          </div>
        </>
      )}

      <Divider style={{ margin: '12px 0' }} />

      <div className="points-section">
        <div className="points-item">
          <span className="points-icon">
            <img src={ORANGE_ICON} alt="credits" style={{ width: 14, height: 14 }} />
          </span>
          <span className="points-label">{t('user.credits', 'Credits')}</span>
          <span className="points-value">{user?.points > 0 ? user.points : '0'}</span>
        </div>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontSize: 12, opacity: 0.9 }}>
          {t('common.language', 'Language')}
        </span>
        <Select
          size="small"
          value={currentLanguage === 'en-US' ? 'en-US' : 'zh-CN'}
          style={{ width: 120 }}
          onChange={(lng) => i18n.changeLanguage(lng)}
          options={[
            { value: 'zh-CN', label: t('common.language_zh', '中文') },
            { value: 'en-US', label: t('common.language_en', 'English') },
          ]}
        />
      </div>

      <Divider style={{ margin: '12px 0' }} />

      <div className="logout-container">
        <div className="logout" onClick={onLogout}>
          <LogoutOutlined />
          <span>{t('user.logout', 'Logout')}</span>
        </div>
      </div>
    </div>
  );
});

export const UnifiedUserAvatar = observer((props: UnifiedAvatarProps) => {
  const store = useContext(EasyDrawContext);
  const user = store.userStore;
  const navigate = useNavigate();
  const [paymentVisible, setPaymentVisible] = useState(false);

  const handleLogout = async () => {
    try {
      await authClient.signOut();
    } finally {
      window.location.href = '/';
    }
  };

  const handleSetting = () => {
    navigate('/setting');
  };

  const handleUpgrade = () => {
    setPaymentVisible(true);
  };

  if (!user.isValidUser) {
    return (
      <>
        <Popover 
          arrow={false} 
          content={
            <UnifiedUnLoginPopoverContent
              handleLogin={() => {
                const callbackURL = `${window.location.pathname}${window.location.search}${window.location.hash}`
                navigate(`${ROUTER_BETTER_AUTH}?callbackURL=${encodeURIComponent(callbackURL)}`)
              }}
              theme={props.theme}
            />
          } 
          placement="bottomRight"
          overlayClassName={props.theme === 'dark' ? 'popover-dark' : ''}
        >
          <Avatar
            className={classNames('user-avatar', props.className)}
            style={{ backgroundColor: '#faa665' }}
            icon={<UserOutlined />}
            size="large"
          />
        </Popover>
      </>
    );
  }

  return (
    <>
      <Popover 
        arrow={false}
        content={
          <UnifiedLoginPopoverContent 
            user={user}
            onSetting={handleSetting}
            onLogout={handleLogout}
            onUpgrade={handleUpgrade}
            theme={props.theme}
          />
        }
        placement="bottomRight"
        overlayClassName={props.theme === 'dark' ? 'popover-dark' : ''}
      >
        {(!user.avatar && !user.nickname && !user.userName) ? (
          <Avatar
            className={classNames('user-avatar', props.className)}
            style={{ backgroundColor: '#faa665' }}
            icon={<UserOutlined />}
            size="large"
          />
        ) : (
          <Avatar
            className={classNames('user-avatar', props.className)}
            src={`${user.avatar || ''}`}
            size="large"
          >
            {user.nickname || user.email?.charAt(0)?.toUpperCase() || user.userName?.charAt(0)?.toUpperCase()}
          </Avatar>
        )}
      </Popover>
      <PlaceholderPayModal
        visible={paymentVisible}
        onClose={() => setPaymentVisible(false)}
        onSuccess={() => {
            user.queryUserInfo(true);
        }}
      />
    </>
  );
});
